import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { KAFFA } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const user = await login(email, password);
      const next = new URLSearchParams(location.search).get("next");
      if (next) {
        if (user.role === "buyer" && user.status !== "approved") {
          navigate("/pending");
        } else {
          navigate(next);
        }
        return;
      }
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "cooperative") navigate("/cooperative");
      else if (user.role === "buyer" && user.status !== "approved") navigate("/pending");
      else navigate("/buyer");
      toast.success(`${t("login.welcome")}, ${user.name}`);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex relative overflow-hidden border-r border-border bg-[#1a0805]">
          <img
            src="https://images.pexels.com/photos/6135326/pexels-photo-6135326.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#5e1f0c]/40 via-transparent to-[#0c0604]/95" />

          {/* Logo */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] flex flex-col items-center pointer-events-none">
            <div className="relative">
              <div className="absolute inset-0 bg-[#e9b87a]/25 blur-3xl rounded-full scale-110" />
              <img
                src="/kaffa-logo.png"
                alt="Kaffa Exchange"
                className="relative h-60 w-60 object-contain drop-shadow-[0_0_40px_rgba(233,184,122,0.4)]"
              />
            </div>
          </div>

          <div className="relative z-10 p-12 lg:p-16 flex flex-col justify-end">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-[#e9b87a] mb-4">
              {t("login.tagline")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter leading-tight text-white">
              {t("login.banner_title")}
            </h2>
            <p className="mt-4 text-white/70 max-w-md">
              {t("login.banner_subtitle")}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-12">
          <form onSubmit={onSubmit} className="w-full max-w-md">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-4">{t("login.signin_label")}</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black mb-8">
              {t("auth.login_title")}
            </h1>

            {err && (
              <div className="mb-4 px-4 py-3 border-l-2 border-destructive bg-destructive/10 text-sm text-destructive">
                {err}
              </div>
            )}

            <div className="space-y-5">
              <div>
                <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t("auth.email")}
                </Label>
                <Input
                  data-testid={KAFFA.loginEmail}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 h-11 rounded-none bg-transparent border-border focus-visible:ring-1 focus-visible:ring-accent"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t("auth.password")}
                </Label>
                <Input
                  data-testid={KAFFA.loginPassword}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 h-11 rounded-none bg-transparent border-border focus-visible:ring-1 focus-visible:ring-accent"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid={KAFFA.loginSubmit}
              className="mt-8 w-full h-12 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em] transition-colors"
            >
              {submitting ? "..." : t("auth.submit_login")}
              <ArrowRight className="h-3.5 w-3.5 ml-2" />
            </Button>

            <p className="mt-6 text-sm text-muted-foreground text-center">
              {t("auth.no_account")}{" "}
              <Link to="/register" className="text-accent hover:underline">{t("auth.request_signup")}</Link>
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
